import { useEffect, useRef } from "react";
import { useRouteError } from "react-router-dom";
import * as Sentry from "@sentry/react";

const AuthRouteError = () => {
  const error = useRouteError();
  const reportedError = useRef<unknown>();

  const isDomMutationError =
    error instanceof Error &&
    (error.name === "NotFoundError" ||
      /removeChild|insertBefore/.test(error.message));

  useEffect(() => {
    // Router errors are handled before they reach the outer RootErrorBoundary.
    if (reportedError.current === error) return;
    reportedError.current = error;
    Sentry.captureException(error, {
      tags: { errorSource: "AuthRouteError" },
    });
  }, [error]);

  return (
    <main className="min-h-screen bg-[#F8F8F8] flex items-center justify-center p-4">
      <section
        role="alert"
        aria-labelledby="auth-error-title"
        className="w-full max-w-md rounded-2xl border border-[#D8D8DD] bg-white p-6"
      >
        <h1
          id="auth-error-title"
          className="text-xl font-semibold text-[#060B13]"
        >
          We couldn’t display this page
        </h1>
        <p className="mt-3 text-sm text-[#475467]">
          Please reload to try again. Details you haven’t submitted may need to
          be entered again.
        </p>
        {isDomMutationError && (
          <p className="mt-3 text-sm text-[#475467]">
            If you’re using page translation, try viewing the original page
            before reloading.
          </p>
        )}
        <p className="mt-3 text-sm text-[#475467]">
          If you already submitted your registration, check your inbox for a
          verification email before creating another account.
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-4">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-full bg-[#FF6600] px-5 py-2.5 text-sm font-semibold text-white"
          >
            Reload page
          </button>
          {/* A full navigation rebuilds DOM that a translator may have changed. */}
          <a
            href="/auth/sign-in"
            className="text-sm font-semibold text-[#FF6600] underline"
          >
            Go to sign in
          </a>
        </div>
      </section>
    </main>
  );
};

export default AuthRouteError;
